import { useState, useEffect, Fragment } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const fmt = (v) => {
  if (v == null) return "—";
  const n = Math.round(v);
  const a = Math.abs(n);
  const s = a >= 1000000 ? `${(a / 1000000).toFixed(2)}M` : a >= 1000 ? `${(a / 1000).toFixed(0)}K` : `${a}`;
  return `${n < 0 ? "-" : ""}$${s}`;
};

// invert=true: lower actual than forecast is GOOD (costs)
const GROUPS = [
  { key: "revenue", label: "Revenue", invert: false },
  { key: "direct", label: "Direct Costs", invert: true },
  { key: "indirect", label: "Indirect", invert: true },
  { key: "overhead", label: "Overhead", invert: true },
  { key: "ebitda", label: "EBITDA", invert: false },
  { key: "net", label: "Net Income", invert: false },
];

const PCT_GROUPS = [
  { key: "gm_pct", label: "GM %" },
  { key: "ebitda_pct", label: "EBITDA %" },
  { key: "net_pct", label: "Net %" },
];

const pcts = (o) => {
  if (!o || !o.revenue) return { gm_pct: null, ebitda_pct: null, net_pct: null };
  return {
    gm_pct: ((o.revenue - o.direct - o.indirect) / o.revenue) * 100,
    ebitda_pct: (o.ebitda / o.revenue) * 100,
    net_pct: (o.net / o.revenue) * 100,
  };
};

export function ComparisonDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.getForecastComparison().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const withActuals = (data?.months || []).filter((m) => m.has_actuals);
  const totals = { forecast: {}, actual: {} };
  GROUPS.forEach((g) => {
    totals.forecast[g.key] = withActuals.reduce((s, m) => s + m.forecast[g.key], 0);
    totals.actual[g.key] = withActuals.reduce((s, m) => s + (m.actual?.[g.key] || 0), 0);
  });

  const deltaCell = (v, invert, isPts) => (
    <td className={cn("px-2 py-1.5 text-right tabular-nums text-[11px] font-semibold",
      v == null ? "text-slate-300" : (invert ? v <= 0 : v >= 0) ? "text-emerald-600" : "text-red-600")}>
      {v == null ? "—" : isPts ? `${v >= 0 ? "+" : ""}${v.toFixed(1)}pt` : `${v >= 0 ? "+" : ""}${fmt(v)}`}
    </td>
  );

  const groupCells = (forecast, actual, keyPrefix) => (
    <>
      {GROUPS.map((g) => (
        <Fragment key={`${keyPrefix}-${g.key}`}>
          <td className="px-2 py-1.5 text-right tabular-nums text-[11px] text-slate-500 border-l border-slate-100">{fmt(forecast[g.key])}</td>
          <td className="px-2 py-1.5 text-right tabular-nums text-[11px] font-medium text-slate-800">{actual ? fmt(actual[g.key]) : "—"}</td>
          {deltaCell(actual ? actual[g.key] - forecast[g.key] : null, g.invert)}
        </Fragment>
      ))}
      {PCT_GROUPS.map((g) => {
        const fp = pcts(forecast)[g.key];
        const ap = actual ? pcts(actual)[g.key] : null;
        return (
          <Fragment key={`${keyPrefix}-${g.key}`}>
            <td className="px-2 py-1.5 text-right tabular-nums text-[11px] text-slate-500 border-l border-slate-100">{fp == null ? "—" : `${fp.toFixed(1)}%`}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-[11px] font-medium text-slate-800">{ap == null ? "—" : `${ap.toFixed(1)}%`}</td>
            {deltaCell(ap != null && fp != null ? ap - fp : null, false, true)}
          </Fragment>
        );
      })}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] max-h-[88vh] overflow-y-auto" data-testid="comparison-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Actuals vs Forecast — {data?.fy_label || "This FY"}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 sticky left-0 bg-white z-10">Month</th>
                  {[...GROUPS, ...PCT_GROUPS].map((g) => (
                    <th key={g.key} colSpan={3} className="text-center px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600 border-l border-slate-200 whitespace-nowrap">{g.label}</th>
                  ))}
                </tr>
                <tr className="border-b border-slate-200 text-[9px] uppercase tracking-wider text-slate-400">
                  <th className="sticky left-0 bg-white z-10" />
                  {[...GROUPS, ...PCT_GROUPS].map((g) => (
                    <>
                      <th key={`${g.key}-f`} className="text-right px-2 py-1 border-l border-slate-200">Fcst</th>
                      <th key={`${g.key}-a`} className="text-right px-2 py-1">Act</th>
                      <th key={`${g.key}-d`} className="text-right px-2 py-1">Δ</th>
                    </>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.months || []).map((m) => (
                  <tr key={m.key} className={cn("border-b border-slate-100", m.has_actuals && "bg-emerald-50/40")} data-testid={`cmp-row-${m.key}`}>
                    <td className={cn("px-2 py-1.5 text-xs whitespace-nowrap text-slate-700 sticky left-0 z-10", m.has_actuals ? "bg-[#f7fefb]" : "bg-white")}>
                      {m.label}
                      {m.has_actuals && <span className="ml-1.5 text-[8px] font-bold uppercase text-emerald-600 bg-emerald-100 rounded px-1 py-0.5">Actuals</span>}
                    </td>
                    {groupCells(m.forecast, m.actual, m.key)}
                  </tr>
                ))}
                {withActuals.length > 0 && (
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold" data-testid="cmp-totals-row">
                    <td className="px-2 py-2 text-xs sticky left-0 bg-slate-50 z-10">Total (actualized)</td>
                    {groupCells(totals.forecast, totals.actual, "tot")}
                  </tr>
                )}
              </tbody>
            </table>
            {withActuals.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">No actuals uploaded yet — use the Actuals button to upload a monthly Divisional Income Statement PDF.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
